import React, { useState, useEffect } from 'react';
import { Key, Lock, CheckCircle2, Trash2, Edit3, Eye, EyeOff, ExternalLink, ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../AuthProvider';
import { saveGeminiApiKeyApi, deleteGeminiApiKeyApi } from '../services/apiService';
import { UserProfile } from '../types';

interface GeminiApiKeySettingsProps {
  onKeyUpdated?: (updatedUser: UserProfile) => void;
  className?: string;
}

export const GeminiApiKeySettings: React.FC<GeminiApiKeySettingsProps> = ({ onKeyUpdated, className = '' }) => {
  const { user, userProfile, setUser, setUserProfile } = useAuth();
  const currentProfile = userProfile || user;

  const [inputApiKey, setInputApiKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // State cục bộ cho maskedKey & hasKey giúp chuyển đổi giao diện tức thì không cần F5
  const [localMaskedKey, setLocalMaskedKey] = useState<string | null>(null);
  const [localHasKey, setLocalHasKey] = useState<boolean | null>(null);

  // Tính toán masked key an toàn
  const computeMaskedKey = (profile: UserProfile | null): string => {
    if (!profile) return '';
    if (profile.geminiApiKeyMasked) return profile.geminiApiKeyMasked;

    const rawKey = profile.geminiApiKey || profile.apiKey || '';
    if (!rawKey || !rawKey.trim()) return '';

    const trimmed = rawKey.trim();
    if (trimmed.length > 8) {
      return `${trimmed.slice(0, 4)}.....${trimmed.slice(-4)}`;
    }
    return '••••••••';
  };

  // Đồng bộ localState từ profile khi profile thay đổi
  useEffect(() => {
    if (currentProfile) {
      const computed = computeMaskedKey(currentProfile);
      if (computed) {
        setLocalMaskedKey(computed);
        setLocalHasKey(true);
      } else if (currentProfile.hasGeminiKey === false) {
        setLocalMaskedKey('');
        setLocalHasKey(false);
      }
    }
  }, [currentProfile?.geminiApiKeyMasked, currentProfile?.hasGeminiKey, currentProfile?.geminiApiKey, currentProfile?.apiKey]);

  // Biến currentMaskedKey và hasKey phản ứng tức thì (Reactive UI)
  const currentMaskedKey = localMaskedKey ?? (currentProfile?.geminiApiKeyMasked || computeMaskedKey(currentProfile));
  const hasKey = localHasKey ?? Boolean(currentMaskedKey || currentProfile?.hasGeminiKey);

  // Auto hide toast after 4 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // 1. CẬP NHẬT STATE KHI LƯU KEY (handleSaveKey / handleSaveApiKey)
  const handleSaveApiKey = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputApiKey.trim()) {
      setToast({ type: 'error', message: 'Vui lòng nhập Gemini API Key.' });
      return;
    }

    setLoading(true);
    try {
      const res = await saveGeminiApiKeyApi(inputApiKey.trim());
      if (res.success || res.user) {
        const masked = res.geminiApiKeyMasked || (res.user && res.user.geminiApiKeyMasked) || computeMaskedKey(res.user);

        // Cập nhật State cục bộ hiển thị ngay lập tức
        setLocalMaskedKey(masked);
        setLocalHasKey(true);

        // Đồng thời cập nhật vào AuthContext
        const updateFn = (prev: UserProfile | null) => {
          if (!prev) return res.user;
          return {
            ...prev,
            ...res.user,
            geminiApiKeyMasked: masked,
            hasGeminiKey: true
          };
        };

        if (setUserProfile) setUserProfile(updateFn as any);
        if (setUser) setUser(updateFn as any);

        if (onKeyUpdated && res.user) {
          onKeyUpdated({
            ...res.user,
            geminiApiKeyMasked: masked,
            hasGeminiKey: true
          });
        }

        // Xóa nội dung vừa nhập trong ô input & chuyển View Masked ngay lập tức
        setInputApiKey('');
        setIsEditing(false);
        setToast({ type: 'success', message: 'Đã mã hóa và lưu Gemini API Key thành công!' });
      }
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Lỗi khi lưu Gemini API Key. Vui lòng thử lại.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = handleSaveApiKey;

  // 2. CẬP NHẬT STATE KHI XÓA KEY (handleDeleteKey / handleDeleteApiKey)
  const handleDeleteApiKey = async () => {
    setLoading(true);
    setShowDeleteModal(false);
    try {
      const res = await deleteGeminiApiKeyApi();
      if (res.success || res.user) {
        // Cập nhật State cục bộ về ô nhập trống ban đầu
        setLocalMaskedKey('');
        setLocalHasKey(false);

        // Đồng thời cập nhật AuthContext
        const updateFn = (prev: UserProfile | null) => {
          if (!prev) return res.user;
          return {
            ...prev,
            ...res.user,
            geminiApiKeyMasked: '',
            hasGeminiKey: false,
            geminiApiKey: '',
            apiKey: ''
          };
        };

        if (setUserProfile) setUserProfile(updateFn as any);
        if (setUser) setUser(updateFn as any);

        if (onKeyUpdated && res.user) {
          onKeyUpdated({
            ...res.user,
            geminiApiKeyMasked: '',
            hasGeminiKey: false,
            geminiApiKey: '',
            apiKey: ''
          });
        }

        setInputApiKey('');
        setIsEditing(false);
        setToast({ type: 'success', message: 'Đã xóa Gemini API Key khỏi tài khoản thành công.' });
      }
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Lỗi khi xóa Gemini API Key.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKey = handleDeleteApiKey;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Toast Notification */}
      {toast && (
        <div
          className={`p-3.5 rounded-xl border flex items-center justify-between text-sm transition-all duration-300 shadow-sm ${
            toast.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          <div className="flex items-center space-x-2">
            {toast.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
            )}
            <span className="font-medium">{toast.message}</span>
          </div>
          <button
            onClick={() => setToast(null)}
            className="text-slate-400 hover:text-slate-600 text-xs font-semibold px-1.5 py-0.5 rounded"
          >
            ✕
          </button>
        </div>
      )}

      {/* Header Info */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Key className="w-5 h-5 text-indigo-600" />
            Cài đặt Gemini API Key
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Mã hóa 256-bit an toàn trên máy chủ. Khóa API của bạn giúp mở khóa toàn bộ sức mạnh AI tại Nhà Giáo Số.
          </p>
        </div>
      </div>

      {/* Display Mode: Saved Masked Key */}
      {hasKey && !isEditing ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg p-3 shadow-xs">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Trạng thái:</span>
                  <span className="inline-flex items-center text-xs font-bold text-emerald-700 bg-emerald-100 px-2.5 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse"></span>
                    Đã cài đặt
                  </span>
                </div>
                <div className="text-sm font-mono font-bold text-slate-800 mt-0.5 tracking-wider">
                  {currentMaskedKey}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors border border-slate-200"
              >
                <Edit3 className="w-3.5 h-3.5 text-slate-600" />
                <span>Đổi Key khác</span>
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors border border-rose-200"
              >
                <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                <span>Xóa</span>
              </button>
            </div>
          </div>

          <div className="text-xs text-slate-500 flex items-center space-x-1.5">
            <Lock className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            <span>Khóa được mã hóa an toàn và bảo vệ khỏi hiển thị mã thô vì lý do bảo mật.</span>
          </div>
        </div>
      ) : (
        /* Input Mode: Enter / Change Key */
        <form onSubmit={handleSaveKey} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
          <div className="space-y-1.5">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">
              {hasKey ? 'Nhập Gemini API Key mới (Đè lên key cũ)' : 'Nhập Google Gemini API Key'}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={inputApiKey}
                onChange={(e) => setInputApiKey(e.target.value)}
                placeholder="AIzaSy..."
                className="w-full bg-white border border-slate-300 rounded-lg py-2.5 pl-3 pr-10 text-sm text-slate-900 font-mono placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-xs"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                title={showPassword ? 'Ẩn API Key' : 'Hiện API Key'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Guidelines Box */}
          <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-3 text-xs text-indigo-900 flex items-start space-x-2.5">
            <ExternalLink className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-medium">Chưa có Gemini API Key?</p>
              <p className="text-slate-600">
                Bạn có thể lấy Gemini API Key miễn phí từ Google AI Studio bằng tài khoản Google cá nhân.
              </p>
              <a
                href="https://aistudio.google.com/app/apikey"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs font-bold text-indigo-600 hover:text-indigo-800 underline hover:no-underline pt-0.5"
              >
                Nhận API Key miễn phí tại Google AI Studio →
              </a>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-2 pt-1">
            {hasKey && (
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setInputApiKey('');
                }}
                className="px-4 py-2 text-xs font-semibold text-slate-600 bg-white hover:bg-slate-100 border border-slate-300 rounded-lg transition-colors"
              >
                Hủy bỏ
              </button>
            )}
            <button
              type="submit"
              disabled={loading || !inputApiKey.trim()}
              className="inline-flex items-center space-x-2 px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Đang mã hóa & lưu...</span>
                </>
              ) : (
                <>
                  <Lock className="w-3.5 h-3.5" />
                  <span>Lưu API Key</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center space-x-3 text-rose-600">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-rose-600" />
              </div>
              <h4 className="text-lg font-bold text-slate-900">Xác nhận xóa API Key</h4>
            </div>

            <p className="text-sm text-slate-600 leading-relaxed">
              Bạn có chắc chắn muốn xóa Gemini API Key này khỏi tài khoản? Các tính năng tạo bài giảng AI có thể yêu cầu bạn cài đặt lại Key sau đó.
            </p>

            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleDeleteKey}
                disabled={loading}
                className="inline-flex items-center space-x-1.5 px-4 py-2 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 rounded-lg transition-colors shadow-xs"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Đang xóa...</span>
                  </>
                ) : (
                  <span>Xác nhận xóa</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeminiApiKeySettings;
